<template>
  <div class="bereich-detail">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <font-awesome-icon :icon="['fas', 'crown']" v-if="hasActivePlan" class="text-warning" />
        {{ selectedTeam?.name }}
      </h2>
      <div>
        <BackButton
          class="me-2"
          fallback="teams-detail"
          :params="selectedTeam ? { id: selectedTeam.id } : {}"
        />
      </div>
    </div>

      <nav aria-label="breadcrumb" class="mb-4">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
              Bereiche
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link
              :to="{ name: 'teams-detail', params: { id: selectedTeam?.id } }"
              class="text-decoration-none"
            >
              {{ selectedTeam?.name || 'Bereich' }}
            </router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            Bereich-Einstellungen
          </li>
        </ol>
      </nav>


    <div v-if="showCustomizationError" class="alert alert-gradient-warning my-2" role="alert">
      <div class="d-flex align-items-center">
        <font-awesome-icon icon="lock" class="fa-2x me-3" />
        <div>
          <h5 class="alert-heading mb-1">Individualisierung nur für Pro Bereiche</h5>
          <p class="mb-2">Diese Funktion ist nur für Pro Bereiche verfügbar.</p>
          <button type="button" class="btn btn-warning btn-sm" @click="upgradeTeam">
            <font-awesome-icon icon="arrow-up" class="me-1" />
            Bereich upgraden
          </button>
        </div>
      </div>
    </div>
    <div>
      <div class="row mb-4">
        <div class="col-12">
          <div class="card h-100">
            <div class="card-body">
              <h5 class="card-title">Beschreibung</h5>
              <p class="card-text">
                {{ selectedTeam?.description || 'Keine Beschreibung vorhanden.' }}
              </p>
            </div>
          </div>
        </div>
      </div>


      <!-- Plan & Upgrade Ribbon -->
      <div
        v-if="isAdminOrCreator && !hasActivePlan"
        class="mb-4"
      >
        <div
          class="card border-0 bg-gradient-primary text-white ribbon-clickable"
          :class="{ 'cursor-pointer': selectedTeam?.status === 'free' }"
          @click="handleRibbonClick"
        >
          <div class="card-body py-3">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <font-awesome-icon icon="crown" class="me-2" />
                <span class="fw-semibold text-white">Plan & Upgrade</span>
              </div>
              <div class="d-flex align-items-center gap-3">
                <div v-if="hasActivePlan" class="text-white">
                  <h6 class="mb-0 text-white">{{ getPlanDisplayName(selectedTeam.status) }}</h6>
                  <small class="text-white-50">{{ getPlanDescription(selectedTeam.status) }}</small>
                </div>
                <div class="d-flex gap-2">
                  <button
                    v-if="hasActivePlan"
                    class="btn btn-warning btn-sm d-flex align-items-center"
                    @click.stop="goToTeamBillingHistory"
                  >
                    <font-awesome-icon icon="history" class="me-1" />
                    <span>Plan verwalten</span>
                    <span :class="getPlanBadgeClass(selectedTeam.status)" class="badge ms-1">
                      {{
                        selectedTeam.status === 'free'
                          ? 'Kostenlos'
                          : selectedTeam.status.toUpperCase()
                      }}
                    </span>
                  </button>
                  <button
                    v-if="selectedTeam?.status === 'free'"
                    class="btn btn-warning btn-sm d-flex align-items-center"
                    @click.stop="goToUpgrade"
                  >
                    <font-awesome-icon icon="crown" class="me-1" />
                    <span>Upgrade</span>
                    <span class="badge bg-primary text-dark ms-1">Pro</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Navigation Cards -->
      <div class="mb-4 nav-cards">
        <!-- Admin/Creator Layout -->
        <div v-if="isAdminOrCreator">
          <!-- Team Management Section -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="cogs" class="me-2" />
              Bereich-Einstellungen
            </h5>
            <div class="row g-3">
              <div class="col-lg-4 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToMembers">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="users" size="2x" class="text-info" />
                    </div>
                    <h6 class="card-title mb-2">Mitglieder verwalten</h6>
                    <p class="card-text small text-muted mb-0">
                      Bereich-Mitglieder einladen und verwalten
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToInvitationCodes">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="key" size="2x" class="text-info" />
                    </div>
                    <h6 class="card-title mb-2">Einladungscodes</h6>
                    <p class="card-text small text-muted mb-0">Einladungscodes verwalten</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToConsumption">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="chart-line" size="2x" class="text-info" />
                    </div>
                    <h6 class="card-title mb-2">Verbrauchsanalyse</h6>
                    <p class="card-text small text-muted mb-0">Bereich-Verbrauch und Statistiken</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-md-6">
                <div
                  class="card h-100 border-0 shadow-sm hover-card"
                  @click="goToTeamBillingHistory"
                >
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="credit-card" size="2x" class="text-info" />
                    </div>
                    <h6 class="card-title mb-2">Abrechnung</h6>
                    <p class="card-text small text-muted mb-0">Plan und Zahlungen verwalten</p>
                    <span class="badge bg-success mt-2 small" v-if="selectedTeam?.status !== 'free'"
                      >Aktiv</span
                    >
                    <span class="badge bg-warning mt-2 small" v-if="selectedTeam?.status === 'free'"
                      >Free</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Individualisierung Section -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="paint-brush" class="me-2" />
              Individualisierung
            </h5>
            <div class="row g-3">
              <div class="col-lg-4 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToTeamEdit">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="edit" size="2x" class="text-info" />
                    </div>
                    <h6 class="card-title mb-2">Bereich bearbeiten</h6>
                    <p class="card-text small text-muted mb-0">
                      Bereichsname und Organisationsfelder verwalten
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToSchemaEdit">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="list-alt" size="2x" class="text-info" />
                    </div>
                    <h6 class="card-title mb-2">Mitgliederfelder bearbeiten</h6>
                    <p class="card-text small text-muted mb-0">
                      Mitgliederfelder und Struktur verwalten
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-md-6">
                <div
                  class="card h-100 border-0 shadow-sm hover-card"
                  @click="goToRefundSettings"
                  :class="{ 'opacity-50': !hasActivePlan }"
                >
                  <div class="card-body text-center py-4">
                    <div class="mb-3 position-relative">
                      <font-awesome-icon
                        :icon="hasActivePlan ? 'money-bill-wave' : 'lock'"
                        size="2x"
                        :class="hasActivePlan ? 'text-info' : 'text-muted'"
                      />
                      <span
                        v-if="!hasActivePlan"
                        class="badge bg-warning position-absolute top-0 start-100 translate-middle small"
                        >Pro</span
                      >
                    </div>
                    <h6 class="card-title mb-2">Erstattung</h6>
                    <p class="card-text small text-muted mb-0">
                      Erstattungseinstellungen konfigurieren
                    </p>
                    <div v-if="hasActivePlan" class="mt-2">
                      <span
                        class="badge"
                        :class="selectedTeam?.refundSettings?.enabled ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ selectedTeam?.refundSettings?.enabled ? 'Aktiviert' : 'Deaktiviert' }}
                      </span>
                    </div>
                    <div v-if="!hasActivePlan" class="mt-2">
                      <button class="btn btn-warning btn-sm" @click.stop="goToUpgrade">
                        <font-awesome-icon icon="crown" class="me-1" />
                        Upgrade
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quittungs-Branding Section -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="receipt" class="me-2" />
              Quittungs-Branding
            </h5>
            <div class="row g-3">
              <div class="col-lg-6 col-md-6">
                <div
                  class="card h-100 border-0 shadow-sm hover-card"
                  @click="goToTeamInfo"
                  :class="{ 'opacity-50': !hasActivePlan }"
                >
                  <div class="card-body text-center py-4">
                    <div class="mb-3 position-relative">
                      <font-awesome-icon
                        :icon="hasActivePlan ? 'info-circle' : 'lock'"
                        size="2x"
                        :class="hasActivePlan ? 'text-primary' : 'text-muted'"
                      />
                      <span
                        v-if="!hasActivePlan"
                        class="badge bg-warning position-absolute top-0 start-100 translate-middle small"
                        >Pro</span
                      >
                    </div>
                    <h6 class="card-title mb-2">Bereich-Information</h6>
                    <p class="card-text small text-muted mb-0">
                      Bereich-Name und Beschreibung anpassen
                    </p>
                    <div v-if="hasActivePlan" class="mt-2">
                      <span
                        class="badge"
                        :class="selectedTeam?.receiptFooterTeamName ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ selectedTeam?.receiptFooterTeamName ? 'Gesetzt' : 'Nicht gesetzt' }}
                      </span>
                    </div>
                    <div v-if="!hasActivePlan" class="mt-2">
                      <button class="btn btn-warning btn-sm" @click.stop="goToUpgrade">
                        <font-awesome-icon icon="crown" class="me-1" />
                        Upgrade
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 col-md-6">
                <div
                  class="card h-100 border-0 shadow-sm hover-card"
                  @click="goToLogo"
                  :class="{ 'opacity-50': !hasActivePlan }"
                >
                  <div class="card-body text-center py-4">
                    <div class="mb-3 position-relative">
                      <font-awesome-icon
                        :icon="hasActivePlan ? 'image' : 'lock'"
                        size="2x"
                        :class="hasActivePlan ? 'text-primary' : 'text-muted'"
                      />
                      <span
                        v-if="!hasActivePlan"
                        class="badge bg-warning position-absolute top-0 start-100 translate-middle small"
                        >Pro</span
                      >
                    </div>
                    <h6 class="card-title mb-2">Logo</h6>
                    <p class="card-text small text-muted mb-0">
                      Bereich-Logo hochladen und verwalten
                    </p>
                    <div v-if="hasActivePlan" class="mt-2">
                      <span
                        class="badge"
                        :class="selectedTeam?.receiptFooterImage ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ selectedTeam?.receiptFooterImage ? 'Hochgeladen' : 'Nicht gesetzt' }}
                      </span>
                    </div>
                    <div v-if="!hasActivePlan" class="mt-2">
                      <button class="btn btn-warning btn-sm" @click.stop="goToUpgrade">
                        <font-awesome-icon icon="crown" class="me-1" />
                        Upgrade
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Designs Section -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="palette" class="me-2" />
              Designs
            </h5>
            <div class="row g-3">
              <div class="col-12">
                <div
                  class="card border-0 shadow-sm design-card hover-card"
                  @click="hasActivePlan ? goToTemplates() : goToUpgrade()"
                  :class="{ 'opacity-50': !hasActivePlan }"
                >
                  <div class="card-body p-4">
                    <div class="row align-items-center">
                      <div class="col-md-2 text-center">
                        <div class="design-icon-wrapper position-relative">
                          <font-awesome-icon
                            :icon="hasActivePlan ? 'file-alt' : 'lock'"
                            size="3x"
                            :class="hasActivePlan ? 'text-warning' : 'text-muted'"
                          />
                          <span
                            v-if="!hasActivePlan"
                            class="badge bg-warning position-absolute top-0 start-100 translate-middle small"
                            >Pro</span
                          >
                        </div>
                      </div>
                      <div class="col-md-8">
                        <h5 class="card-title mb-2">Bereich-Designs bearbeiten</h5>
                        <div class="card-text text-muted mb-2">
                          <ul class="list-unstyled mb-0">
                            <li class="mb-2 d-flex align-items-start">
                              <span
                                class="badge bg-primary rounded-pill me-2 mt-1 d-flex align-items-center justify-content-center"
                                style="min-width: 20px; height: 20px; font-size: 0.7rem;"
                                >1</span
                              >
                              <span
                                >Ein Bereich kann mehrere
                                <strong class="text-primary">Designs</strong> verwenden</span
                              >
                            </li>
                            <li class="mb-2 d-flex align-items-start">
                              <span
                                class="badge bg-primary rounded-pill me-2 mt-1 d-flex align-items-center justify-content-center"
                                style="min-width: 20px; height: 20px; font-size: 0.7rem;"
                                >2</span
                              >
                              <span
                                >Für Designs können bestimmte
                                <strong class="text-primary">Felder</strong> verwendet werden</span
                              >
                            </li>
                            <li class="mb-0 d-flex align-items-start">
                              <span
                                class="badge bg-primary rounded-pill me-2 mt-1 d-flex align-items-center justify-content-center"
                                style="min-width: 20px; height: 20px; font-size: 0.7rem;"
                                >3</span
                              >
                              <span
                                ><strong class="text-primary">Vorausgefüllte Vorlagen</strong> können
                                erstellt werden</span
                              >
                            </li>
                          </ul>
                        </div>
                        <div class="d-flex align-items-center gap-3">
                          <span v-if="hasActivePlan" class="badge bg-warning text-dark">
                            <font-awesome-icon icon="palette" class="me-1" />
                            {{ teamTemplateCount }} Designs verfügbar
                          </span>
                          <button
                            v-if="!hasActivePlan"
                            class="btn btn-warning btn-sm"
                            @click.stop="goToUpgrade"
                          >
                            <font-awesome-icon icon="crown" class="me-1" />
                            Upgrade
                          </button>
                        </div>
                      </div>
                      <div class="col-md-2 text-end">
                        <font-awesome-icon icon="chevron-right" size="lg" class="text-muted" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!hasActivePlan" class="alert alert-gradient-warning mb-4" role="alert">
            <div class="d-flex align-items-center">
              <font-awesome-icon icon="lock" class="fa-2x me-3" />
              <div>
                <h5 class="alert-heading mb-1">Individualisierung nur für Pro Bereiche</h5>
                <p class="mb-2">Diese Funktion ist nur für Pro Bereiche verfügbar.</p>
                <button type="button" class="btn btn-warning btn-sm" @click="upgradeTeam">
                  <font-awesome-icon icon="arrow-up" class="me-1" />
                  Bereich upgraden
                </button>
              </div>
            </div>
          </div>

          <!-- Quittungen & Finanzen Section -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="receipt" class="me-2" />
              Quittungen
            </h5>
            <div class="row g-3">
              <div class="col-lg-6 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="openTransferDialog">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="exchange-alt" size="2x" class="text-success" />
                    </div>
                    <h6 class="card-title mb-2">Quittungs-Transfer</h6>
                    <p class="card-text small text-muted mb-0">
                      Quittungen zwischen Konten übertragen
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Meine Aktivitäten Section (für Admins) -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="user" class="me-2" />
              Meine Einstellungen
            </h5>
            <div class="row g-3">
              <div class="col-lg-6 col-md-6">
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToFields">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="list-alt" size="2x" class="text-primary" />
                    </div>
                    <h6 class="card-title mb-2">Meine Antworten</h6>
                    <p class="card-text small text-muted mb-0">
                      Mitgliederfelder ausfüllen und bearbeiten
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 col-md-6">
                <div
                  class="card h-100 border-0 shadow-sm hover-card"
                  @click="goToRefund"
                  :class="{ 'opacity-50': !canAccessRefund }"
                >
                  <div class="card-body text-center py-4">
                    <div class="mb-3 position-relative">
                      <font-awesome-icon
                        :icon="canAccessRefund ? 'money-bill-wave' : 'lock'"
                        size="2x"
                        :class="canAccessRefund ? 'text-primary' : 'text-muted'"
                      />
                      <span
                        v-if="!canAccessRefund"
                        class="badge bg-warning position-absolute top-0 start-100 translate-middle small"
                        >Pro</span
                      >
                    </div>
                    <h6 class="card-title mb-2">Meine Erstattungseinstellungen</h6>
                    <p class="card-text small text-muted mb-0">
                      Meine persönlichen Erstattungsdaten verwalten
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Team Member Layout -->
        <div v-else>
          <!-- Meine Aktivitäten Section -->
          <div class="mb-4">
            <h5 class="text-muted mb-3 d-flex align-items-center">
              <font-awesome-icon icon="user" class="me-2" />
              Meine Einstellungen
            </h5>
            <div class="row g-3">
              <div
                class="col-md-6"
                :class="{ 'col-lg-6': !canAccessRefund, 'col-lg-4': canAccessRefund }"
              >
                <div class="card h-100 border-0 shadow-sm hover-card" @click="goToFields">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="list-alt" size="2x" class="text-primary" />
                    </div>
                    <h6 class="card-title mb-2">Meine Antworten</h6>
                    <p class="card-text small text-muted mb-0">
                      Mitgliederfelder ausfüllen und bearbeiten
                    </p>
                  </div>
                </div>
              </div>
              <div
                class="col-md-6"
                :class="{ 'col-lg-6': !canAccessRefund, 'col-lg-4': canAccessRefund }"
              >
                <div class="card h-100 border-0 shadow-sm hover-card" @click="openTransferDialog">
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="exchange-alt" size="2x" class="text-success" />
                    </div>
                    <h6 class="card-title mb-2">Quittungs-Transfer</h6>
                    <p class="card-text small text-muted mb-0">Quittungen übertragen</p>
                  </div>
                </div>
              </div>
              <div
                class="col-md-6"
                :class="{ 'col-lg-6': !canAccessRefund, 'col-lg-4': canAccessRefund }"
              >
                <div
                  class="card h-100 border-0 shadow-sm hover-card"
                  @click="goToRefund"
                  v-if="canAccessRefund"
                >
                  <div class="card-body text-center py-4">
                    <div class="mb-3">
                      <font-awesome-icon icon="money-bill-wave" size="2x" class="text-primary" />
                    </div>
                    <h6 class="card-title mb-2">Erstattungseinstellungen</h6>
                    <p class="card-text small text-muted mb-0">Meine Erstattungsdaten verwalten</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Team Statistics -->
      <div class="card mb-4 hover-card" v-if="isAdminOrCreator">
        <div class="card-header">
          <h5 class="mb-0">
            <font-awesome-icon icon="chart-bar" class="me-2" />
            Bereich-Statistiken
          </h5>
        </div>
        <div class="card-body">
          <div class="row g-4">
            <div class="col-md-3">
              <div class="text-center">
                <div class="display-6 text-primary mb-2">{{ totalMembers }}</div>
                <div class="fw-semibold">Mitglieder</div>
                <small class="text-muted">Gesamt</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="text-center">
                <div class="display-6 text-info mb-2">{{ adminCount }}</div>
                <div class="fw-semibold">Administratoren</div>
                <small class="text-muted">Bereich-Admins</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="text-center">
                <div class="display-6 text-success mb-2">{{ totalReceiptsCount }}</div>
                <div class="fw-semibold">Ausgefüllte Quittungen</div>
                <small class="text-muted">Gesamt erstellt</small>
              </div>
            </div>
            <div class="col-md-3">
              <div class="text-center">
                <div class="display-6 text-warning mb-2">
                  {{ selectedTeam?.account?.amount || 0 }}
                </div>
                <div class="fw-semibold">Quittungen</div>
                <small class="text-muted">Verfügbar</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gefahrenzone -->
      <div
        class="card quitly-danger-zone-card mb-4"
        :class="{
          'quitly-danger-zone-collapsed': !isDangerZoneExpanded,
          'quitly-danger-zone-expanded': isDangerZoneExpanded,
        }"
        v-if="canDelete || !canDelete"
      >
        <!-- Toggle Header with Chevron -->
        <div
          class="quitly-danger-zone-header d-flex justify-content-between align-items-center"
          role="button"
          @click="toggleDangerZone"
          :aria-expanded="isDangerZoneExpanded"
          aria-controls="dangerZone"
        >
          <h5 class="quitly-danger-zone-title mx-auto">Gefahrenbereich</h5>
          <font-awesome-icon
            :icon="['fas', 'chevron-down']"
            :class="['chevron-icon', { rotated: isDangerZoneExpanded }]"
            style="padding-right: 0.5rem"
          />
        </div>
        <div id="dangerZone" :class="['collapse', { show: isDangerZoneExpanded }]">
          <div class="card-body">
            <p class="text-danger fw-bold text-center mb-3">
              Achtung: Diese Aktionen können nicht rückgängig gemacht werden!
            </p>
          </div>
          <div class="card-body pt-0">
            <div class="d-flex flex-column gap-2">
              <button class="btn btn-outline-danger w-100" @click="leaveTeam">
                <font-awesome-icon icon="right-from-bracket" class="me-2" /> Bereich verlassen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Transfer Dialog -->
    <CoinTransferDialog
      v-if="showTransferDialog"
      :show="showTransferDialog"
      :teamId="selectedTeam.id"
      :personalCoins="personalCoins"
      :teamCoins="selectedTeam.account.amount"
      @close="showTransferDialog = false"
      @transferCompleted="handleTransferCompleted"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { showSuccess, showError, showLeaveTeamModal } from '@/services/notification'
import { teamService } from '@/api/teams'
import { receiptsApi } from '@/api/receipts'
import { templateService } from '@/api/templates'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { useTeamIntegrityChecks } from '@/composables/useTeamIntegrityChecks'

import CoinTransferDialog from '../coins/CoinTransferDialog.vue'

const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const { selectedTeam } = storeToRefs(teamsStore)

const { refreshTeams, loadTeamById } = teamsStore

const currentUser = computed(() => authStore.user)

const {
  isAdminOrCreator,
  checkMissingRequiredFields,
  checkRefundDataRequirements,
} = useTeamIntegrityChecks({
  selectedTeam,
  currentUser,
  loadTeamById,
  refreshTeams,
})

const isDangerZoneExpanded = ref(false)

const showTransferDialog = ref(false)
const personalCoins = ref(0)

const teamTemplateCount = ref(0)

const router = useRouter()
const route = useRoute()

const showCustomizationError = computed(() => {
  return route.query.error === 'customization_requires_pro' && route.query.from === 'customization'
})

const canDelete = computed(() => {
  if (!selectedTeam.value || !currentUser.value) return false

  if (selectedTeam.value.creatorId === currentUser.value.id) {
    return true
  }
  return false
})

const canAccessRefund = computed(() => {
  const hasValidStatus =
    selectedTeam.value?.status === 'pro' || selectedTeam.value?.status === 'ultimate'

  const isRefundEnabled = selectedTeam.value?.refundSettings?.enabled === true

  return hasValidStatus && isRefundEnabled
})

const hasActivePlan = computed(() => {
  return selectedTeam.value?.status === 'pro' || selectedTeam.value?.status === 'ultimate'
})

const hasAvailableCoins = computed(() => {
  return selectedTeam.value?.account?.amount > 0
})

const totalMembers = computed(() => {
  return selectedTeam.value?.member?.length || 0
})

const adminCount = computed(() => {
  if (!selectedTeam.value?.member) return 0
  return selectedTeam.value.member.filter((member) => member.role === 'teamAdmin').length
})

const totalFieldsCount = computed(() => {
  return selectedTeam.value?.fields?.length || 0
})

const completedFieldsCount = computed(() => {
  if (!selectedTeam.value?.fields || !currentUser.value) return 0

  return selectedTeam.value.fields.filter((field) => {
    if (!field.required) return true

    if (field.responses && Array.isArray(field.responses)) {
      const userResponse = field.responses.find(
        (response) => response.userId === currentUser.value.id,
      )
      return userResponse && userResponse.data && userResponse.data !== ''
    }

    if (Array.isArray(field.response)) {
      return field.response.length > 0 && field.response[0]?.data
    }

    return false
  }).length
})

const totalReceiptsCount = ref(0)

const goToInvitationCodes = () => {
  router.push({ name: 'teams-invitation-codes', params: { id: selectedTeam.value.id } })
}

const goToConsumption = () => {
  router.push({ name: 'teams-consumption', params: { id: selectedTeam.value.id } })
}

const goToMembers = () => {
  router.push({ name: 'teams-members', params: { id: selectedTeam.value.id } })
}

const goToFields = () => {
  router.push({ name: 'teams-fields', params: { id: selectedTeam.value.id } })
}

const goToTeamEdit = () => {
  router.push({ name: 'teams-edit', params: { id: selectedTeam.value.id } })
}

const goToSchemaEdit = () => {
  router.push({ name: 'teams-schema-edit', params: { id: selectedTeam.value.id } })
}

const goToTeamInfo = () => {
  router.push({ name: 'teams-customization-team-info', params: { id: selectedTeam.value.id } })
}

const goToLogo = () => {
  router.push({ name: 'teams-customization-logo', params: { id: selectedTeam.value.id } })
}

const goToRefundSettings = () => {
  router.push({ name: 'teams-customization-refund', params: { id: selectedTeam.value.id } })
}

const goToTemplates = () => {
  router.push({ name: 'teams-customization-templates', params: { id: selectedTeam.value.id } })
}

const goToRefund = () => {
  if (!selectedTeam.value) {
    showError('Bereich nicht gefunden')
    return
  }

  const hasValidStatus =
    selectedTeam.value.status === 'pro' || selectedTeam.value.status === 'ultimate'
  const isRefundEnabled = selectedTeam.value.refundSettings?.enabled === true

  if (!hasValidStatus) {
    showError('Erstattungsfunktion ist nur für Pro und Ultimate Bereiche verfügbar')
    return
  }

  if (!isRefundEnabled) {
    showError(
      'Erstattung ist für diesen Bereich nicht aktiviert. Bitte kontaktiere den Bereich-Administrator.',
    )
    return
  }

  router.push({ name: 'teams-refund', params: { id: selectedTeam.value.id } })
}

const goToUpgrade = () => {
  router.push({ name: 'teams-upgrade', params: { id: selectedTeam.value.id } })
}

const goToTeamBillingHistory = () => {
  router.push({ name: 'teams-upgrade', params: { id: selectedTeam.value.id } })
}

const handleRibbonClick = () => {
  if (hasActivePlan.value) {
    goToTeamBillingHistory()
  } else if (selectedTeam.value?.status === 'free') {
    goToUpgrade()
  }
}

const getPlanDisplayName = (status) => {
  const plans = {
    free: 'Free Plan',
    pro: 'Pro Plan',
    ultimate: 'Ultimate Plan',
  }
  return plans[status] || 'Unbekannter Plan'
}

const getPlanDescription = (status) => {
  const descriptions = {
    free: 'Grundlegende Funktionen',
    pro: 'Erweiterte Funktionen ',
    ultimate: 'Alle Funktionen',
  }
  return descriptions[status] || 'Keine Beschreibung verfügbar'
}

const getPlanBadgeClass = (status) => {
  const classes = {
    free: 'bg-secondary',
    pro: 'bg-warning text-dark',
    ultimate: 'bg-danger',
  }
  return classes[status] || 'bg-secondary'
}

const upgradeTeam = () => {
  router.push({ name: 'teams-upgrade', params: { id: selectedTeam.value.id } })
}

const goToCreateReceipt = () => {
  router.push({
    name: 'receipt-create',
    query: {
      teamId: selectedTeam.value.id,
      teamName: selectedTeam.value.name,
      from: 'team',
      preselectedTeam: 'true',
    },
  })
}

const openTransferDialog = async () => {
  if (currentUser.value?.account?.amount) {
    personalCoins.value = currentUser.value.account.amount
  } else {
    await authStore.updateProfile()
    personalCoins.value = authStore.user?.account?.amount || 0
  }

  showTransferDialog.value = true
}

const handleTransferCompleted = async (transferData) => {
  await teamsStore.loadTeamById(selectedTeam.value.id)
  await refreshTeams()

  await authStore.updateProfile()
  showSuccess(`Transfer von ${transferData.amount} Quittungen erfolgreich!`)
}

const leaveTeam = async () => {
  if (!selectedTeam.value) return

  const result = await showLeaveTeamModal(selectedTeam.value.name)

  if (!result.isConfirmed) {
    return
  }

  try {
    await teamService.removeMember(selectedTeam.value.id, currentUser.value.id)
    showSuccess('Du hast den Bereich verlassen')

    teamsStore.clearSelectedTeam()
    await teamsStore.fetchTeams()
    router.push({ name: 'teams-list' })
  } catch (err) {
    showError('Bereich konnte nicht verlassen werden')
  }
}

const loadReceiptStats = async () => {
  if (!selectedTeam.value?.id) return

  try {
    let result

    if (isAdminOrCreator.value) {
      result = await receiptsApi.getTeamReceipts(selectedTeam.value.id, {
        page: 1,
        limit: 1,
      })
    } else {
      result = await receiptsApi.getUserReceipts({
        page: 1,
        limit: 1,
        teamId: selectedTeam.value.id,
      })
    }

    totalReceiptsCount.value = result.pagination?.total || 0
  } catch (err) {
    console.error('Fehler beim Laden der Quittungsstatistiken:', err)
  }
}

const loadTeamTemplates = async () => {
  if (!selectedTeam.value?.id) return

  try {
    const response = await templateService.getTeamTemplates(selectedTeam.value.id)
    teamTemplateCount.value = response.data?.length || 0
  } catch (err) {
    console.error('Error loading team templates:', err)
    teamTemplateCount.value = 0
  }
}

const toggleDangerZone = () => {
  isDangerZoneExpanded.value = !isDangerZoneExpanded.value
}

onMounted(async () => {
  await Promise.all([
    checkMissingRequiredFields(),
    checkRefundDataRequirements(),
    loadReceiptStats(),
  ])
  await loadTeamTemplates()
})

watch(
  () => selectedTeam.value?.id,
  async (newId, oldId) => {
    if (!newId || newId === oldId) return
    await loadTeamTemplates()
  },
)
</script>

<style scoped>
.opacity-50 {
  opacity: 0.5;
  cursor: not-allowed;
}

.opacity-50:hover {
  transform: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border-color: #e9ecef !important;
}
</style>
